"use client";

import Link from "next/link";
import { SiOpenai, SiPerplexity, SiClaude } from "@/components/icons";
import { HiExternalLink } from "@/components/icons";
import { trackEvent } from "@/lib/analytics/events";

interface AskAICTAProps {
  prompt: string;
  title?: string;
  subtitle?: string;
  source?: string;
  className?: string;
}

const platforms = [
  {
    key: "chatgpt",
    label: "Ask ChatGPT",
    icon: SiOpenai,
    baseUrl: "https://chatgpt.com/?q=",
  },
  {
    key: "perplexity",
    label: "Ask Perplexity",
    icon: SiPerplexity,
    baseUrl: "https://www.perplexity.ai/search/new?q=",
  },
  {
    key: "claude",
    label: "Ask Claude",
    icon: SiClaude,
    baseUrl: "https://claude.ai/new?q=",
  },
] as const;

export default function AskAICTA({
  prompt,
  title = "Get an Unbiased AI Perspective",
  subtitle = "Don\u2019t take our word for it \u2014 ask AI to analyze this tool independently.",
  source,
  className,
}: AskAICTAProps) {
  const encoded = encodeURIComponent(prompt);

  const handleClick = (platform: string) => {
    trackEvent("ask_ai_click", {
      event_category: "engagement",
      ai_platform: platform,
      source: source || "unknown",
    });
  };

  return (
    <section className={`container mx-auto px-4 mb-24 ${className || ""}`}>
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2.5rem] border border-primary/20 bg-gradient-to-br from-primary/[0.04] via-orange-500/[0.03] to-transparent p-8 md:p-12 overflow-hidden">
          {/* Radial glow — signature warm atmosphere */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[600px] h-[400px] bg-[radial-gradient(ellipse_at_center,_rgba(249,116,21,0.08)_0%,_transparent_70%)] pointer-events-none" />

          {/* Noise overlay — brand texture */}
          <div className="absolute inset-0 opacity-[0.03] bg-noise mix-blend-overlay pointer-events-none" />

          {/* Grid pattern — structural depth */}
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.06] pointer-events-none" />

          <div className="relative z-10">
            {/* Header */}
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{title}</h2>
              <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                {subtitle}
              </p>
            </div>

            {/* Gradient divider */}
            <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent mb-10" />

            {/* Platform Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {platforms.map(({ key, label, icon: Icon, baseUrl }) => (
                <Link
                  key={key}
                  href={`${baseUrl}${encoded}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleClick(key)}
                  className="group relative flex items-center justify-center gap-3 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm px-6 py-4 font-semibold transition-all duration-300 hover:border-primary/40 hover:bg-primary/[0.04] hover:shadow-lg hover:shadow-primary/10 hover:scale-[1.02]"
                >
                  <Icon className="w-5 h-5 text-foreground/70 transition-colors duration-300 group-hover:text-primary" />
                  <span>{label}</span>
                  <HiExternalLink className="w-3.5 h-3.5 text-muted-foreground/50 transition-all duration-300 group-hover:text-primary/60" />
                  <span className="sr-only">(opens in new tab)</span>
                </Link>
              ))}
            </div>

            {/* Disclaimer */}
            <p className="text-center text-xs text-muted-foreground mt-8">
              AI responses are generated independently and may vary
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
